"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Image from "next/image";

import convertHTMLtoJSX from "@/next-src/utils/convertHTMLtoJSX";
import ButtonLink, { ACTIONS } from "@/next-src/components/ui/button-link/ButtonLink";
import { useBrochureUpdate } from "@/next-src/context/Brochure";
import useWindowDimensions from "@/next-src/hooks/useWindowDimensions";
import ResponsiveImage from "@/next-src/components/ui/responsive-image/ResponsiveImage";

import comCSS from "./Brochure.module.scss";

const minTranslateY = -100;
const maxTranslateY = 100;

const Brochure = ({ className = "", data = {} }) => {
  const [translateY, setTranslateY] = useState(minTranslateY);
  const wrapperRef = useRef();
  const { setIsShown } = useBrochureUpdate();
  const { width: windowWidth } = useWindowDimensions();
  const {
    preTitleMobile,
    titleMobile,
    textMobile,
    buttonMobile,
    bgColorMobile,
    file,
    bgImage,
  } = data || {};

  let { preTitle, title, text, bgColor, button } = data || {};

  if (windowWidth < 992) {
    preTitle = preTitleMobile;
    title = titleMobile;
    text = textMobile;
    bgColor = bgColorMobile;
    button = buttonMobile;
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      const onScroll = () => {
        if (!wrapperRef?.current) return;

        const { top, bottom } = wrapperRef.current.getBoundingClientRect();

        const isInView = top <= window.innerHeight && bottom >= 0;
        if (isInView) {
          const curTranslateY = Math.round(
            (1 - top / window.innerHeight) * (maxTranslateY - minTranslateY) +
              minTranslateY
          );
          setTranslateY(curTranslateY);
        } else if (bottom < 0) {
          setTranslateY(maxTranslateY);
        } else {
          setTranslateY(minTranslateY);
        }
      };

      window.addEventListener("scroll", onScroll, false);
      window.addEventListener("resize", onScroll, false);

      // Call handler right away so state gets updated with initial window size
      onScroll();

      return () => {
        window.removeEventListener("scroll", onScroll, false);
        window.removeEventListener("resize", onScroll, false);
      };
    }
  }, []);

  const isDownloadIcon = button.action === ACTIONS.SHOW_BROCHURE;

  return (
    <section
      ref={wrapperRef}
      className={cn(className, comCSS.wrapper, "section add-line-top")}
      style={{
        backgroundColor: bgColor,
      }}
    >
      <div className="padding-global">
        <div className="container-large">
          <div className="padding-section-medium">
            <div className="brochure-large-tile">
              <div className="brochure-tile-inner-wrap">
                <div className="max-width-xsmall">
                  {preTitle ? (
                    <div className="margin-bottom margin-small">
                      {convertHTMLtoJSX(preTitle)}
                    </div>
                  ) : null}
                  <div className="margin-bottom margin-small">
                    {convertHTMLtoJSX(title)}
                  </div>
                  <div className="max-width-xxsmall">
                    {convertHTMLtoJSX(text)}
                  </div>
                  <div className="margin-top margin-medium">
                    <div className="display-vertical-flex-left-top">
                      <ButtonLink
                        data={button}
                        className="is-icon is-inverted"
                        isDownloadIcon={isDownloadIcon}
                        onClick={(e) => {
                          setIsShown(true);
                          e.preventDefault();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="brochure-tile-bg-wrap">
                <Image
                  width={1280}
                  height={602}
                  src="/images/home/64122fa42608817fcc2f5a3a_brochure-shadow.png"
                  loading="lazy"
                  sizes="90vw"
                  alt="'"
                  className="brochure-tile-img is-shadow"
                  style={{
                    willChange: "transform",
                    transform: `translate3d(0px, ${translateY}px, 0px) scale3d(1, 1.35, 1) rotateX(0deg) rotateY(0deg) rotateZ(0deg) skew(0deg, 0deg)`,
                    transformStyle: "preserve-3d",
                  }}
                />
                <ResponsiveImage
                  width={1280}
                  height={602}
                  src={bgImage?.src}
                  lazyLoading
                  sizes="90vw"
                  alt={bgImage?.alt}
                  className="brochure-tile-img"
                />
                <div className="brochure-specifications-wrap">
                  {file?.type ? (
                    <div className="rounded-tag">
                      <div>{file?.type?.toUpperCase()}</div>
                    </div>
                  ) : null}
                  {file?.size ? (
                    <div className="rounded-tag">
                      <div>{file?.size}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

Brochure.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    preTitle: PropTypes.string,
    preTitleMobile: PropTypes.string,
    title: PropTypes.string,
    titleMobile: PropTypes.string,
    text: PropTypes.string,
    textMobile: PropTypes.string,
    button: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      action: PropTypes.string
    }),
    buttonMobile: PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      bgColor: PropTypes.string,
      bgColorHover: PropTypes.string,
      borderColor: PropTypes.string,
      borderColorHover: PropTypes.string,
      hide: PropTypes.bool,
      action: PropTypes.string
    }),
    file: PropTypes.shape({
      type: PropTypes.string,
      size: PropTypes.string,
    }),
    bgImage: PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    bgColor: PropTypes.string,
    bgColorMobile: PropTypes.string,
  }),
};

export default Brochure;
